import { Box } from "@mantine/core";
import React, { useEffect, useMemo } from "react";
import { ButtonRadioSelect } from "../../../stories/ButtonRadioSelect/ButtonRadioSelect";
import { Title } from "../../../stories/Title/Title";
import { DentalSvgWithButtons } from "../../../stories/DentalSvgWithButtons/DentalSvgWithButtons";
import { Controller, useFormContext } from "react-hook-form";
import { getArchFromTeeth } from "../../../utils/product.utils";
import { Product } from "../../../types/interfaces";
import { ArchType, ItemMaterials } from "../../../types/enums";
import {
  getDisabledTeeth,
  HookPlacementBy,
  PartialDentureOptions,
} from "../../../utils/workflows.utils";

export const HooksAndOptions: React.FC<{
  product: Product;
  hideCustomTray?: boolean;
}> = ({ product, hideCustomTray = false }) => {
  const {
    control,
    watch,
    formState: { errors },
    setValue,
  } = useFormContext();
  const hookChoice = watch("hookChoice");
  const watchMaterial = product.removableItem[0]?.itemMaterial;
  const watchMaterial1 = product.removableItem[1]?.itemMaterial;

  const disabledTeeth = useMemo(
    () => [
      ...product.removableItem.map(item => item.teeth).flat(),
      ...getDisabledTeeth(product.teeth || []),
    ],
    [JSON.stringify(product.removableItem ?? [])]
  );

  const hooks = product.removableItem.reduce((acc, item) => {
    if (item.partialDenture?.hooks) {
      acc.push(...item.partialDenture?.hooks);
    }
    return acc;
  }, []);

  const getTeethsByMaterial = (material: ItemMaterials) => {
    return hooks.reduce((acc, value) => {
      if (value.hooksMaterial?.id === material) {
        acc.push(...value.hooksPosition);
      }
      return acc;
    }, []);
  };
  const defaultHooksMaterialOptions: {
    label: string;
    name: string;
    value: string;
    key: string;
    description?: string;
    color: string;
    teeth: number[];
  }[] = [
    {
      label: "Métal",
      name: "metal",
      value: ItemMaterials.METAL_NON_PRECIOUS,
      key: ItemMaterials.METAL_NON_PRECIOUS,
      color: "#3C61E3",
      teeth: getTeethsByMaterial(ItemMaterials.METAL_NON_PRECIOUS),
    },
    {
      label: "Valplast - option esthétique (crochet rose)",
      name: "valplast",
      value: ItemMaterials.VALPLAST,
      key: ItemMaterials.VALPLAST,
      color: "#ff4da6",
      teeth: getTeethsByMaterial(ItemMaterials.VALPLAST),
    },
    {
      label: "Acetal - option esthétique (crochet blanc)",
      name: "acetal",
      value: ItemMaterials.ACETAL,
      key: ItemMaterials.ACETAL,
      color: "#66ccff",
      teeth: getTeethsByMaterial(ItemMaterials.ACETAL),
    },
  ];
  const dentalSvgWithButtonsChoices = useMemo(() => {
    const returnOptions = [...defaultHooksMaterialOptions];
    if (
      (watchMaterial && watchMaterial.id === ItemMaterials.ACRYLIC) ||
      (watchMaterial1 && watchMaterial1.id === ItemMaterials.ACRYLIC)
    ) {
      returnOptions.unshift({
        label: "Pas de crochet",
        value: "no-hook",
        key: "no-hook",
        color: "transparent",
        teeth: [],
        name: "no-hook",
      });
    }
    return returnOptions;
  }, [watchMaterial, watchMaterial1]);

  const options = useMemo(() => {
    const archType = getArchFromTeeth(product.teeth || []);
    const returnOptions = [
      {
        label: "Grille de renfort métallique",
        value: PartialDentureOptions.METAL_MESH,
      },
    ];

    if (!hideCustomTray) {
      returnOptions.push({
        label: "Porte-empreinte individuel",
        value: PartialDentureOptions.CUSTOM_TRAY,
      });
    }
    if (archType === ArchType.MANDIBULAIRE) {
      returnOptions.push({
        label: "Bandeau métallique lingual",
        value: PartialDentureOptions.LINGUAL_METAL_BAND,
      });
    }
    if (archType === ArchType.MAXILLAIRE) {
      returnOptions.push({
        label: "Bandeau métallique palatal",
        value: PartialDentureOptions.PALATAL_METAL_BAND,
      });
    }
    if (archType === ArchType.BIMAXILAIRE) {
      returnOptions.push(
        {
          label: "Bandeau métallique palatal",
          value: PartialDentureOptions.PALATAL_METAL_BAND,
        },
        {
          label: "Bandeau métallique lingual",
          value: PartialDentureOptions.LINGUAL_METAL_BAND,
        }
      );
    }
    return returnOptions;
  }, [product.teeth, hideCustomTray]);

  useEffect(() => {
    if (
      !product?.removableItem?.length ||
      !product?.removableItem?.[0]?.partialDenture
    )
      return;

    const hookChoice = hooks.reduce((acc, value) => {
      if (value.hooksPosition.length > 0) {
        return HookPlacementBy.DOCTOR;
      }
      return acc;
    }, HookPlacementBy.JASPER);

    setValue("hookChoice", hookChoice);

    const options = [];
    if (
      product.removableItem?.[0]?.partialDenture?.customTray ||
      (product.removableItem.length > 1 &&
        product.removableItem?.[1]?.partialDenture?.customTray)
    )
      options.push(PartialDentureOptions.CUSTOM_TRAY);
    if (
      product.removableItem?.[0]?.partialDenture?.metalMesh ||
      (product.removableItem.length > 1 &&
        product.removableItem?.[1]?.partialDenture?.metalMesh)
    )
      options.push(PartialDentureOptions.METAL_MESH);
    if (
      product.removableItem?.[0]?.partialDenture?.palatalMetalBand ||
      (product.removableItem.length > 1 &&
        product.removableItem?.[1]?.partialDenture?.palatalMetalBand)
    )
      options.push(PartialDentureOptions.PALATAL_METAL_BAND);
    if (
      product.removableItem?.[0]?.partialDenture?.lingualMetalBand ||
      (product.removableItem.length > 1 &&
        product.removableItem?.[1]?.partialDenture?.lingualMetalBand)
    )
      options.push(PartialDentureOptions.LINGUAL_METAL_BAND);
    setValue("options", options);
  }, [product]);

  useEffect(() => {
    if (hookChoice === HookPlacementBy.DOCTOR) {
      setValue("hooksPosition", {
        metal: getTeethsByMaterial(ItemMaterials.METAL_NON_PRECIOUS),
        valplast: getTeethsByMaterial(ItemMaterials.VALPLAST),
        acetal: getTeethsByMaterial(ItemMaterials.ACETAL),
      });
      setValue("hooksMaterial", null);
    } else {
      setValue("hooksMaterial", hooks?.[0]?.hooksMaterial?.id ?? "no-hook");
      setValue("hooksPosition", {
        metal: [],
        valplast: [],
        acetal: [],
      });
    }
  }, [hookChoice, hooks]);
  return (
    <Box pt="lg">
      <Title title="Détails concernant les crochets" size={3} mb="md" />
      <Controller
        control={control}
        name="hookChoice"
        rules={{ required: "Veuillez sélectionner une option" }}
        render={({ field }) => (
          <ButtonRadioSelect
            options={[
              {
                label: "Jasper choisit pour vous",
                value: HookPlacementBy.JASPER,
                description:
                  "Nous positionnons les crochets de la façon la plus esthétique possible",
              },
              {
                label: "Choix du praticien",
                value: HookPlacementBy.DOCTOR,
                description:
                  "Sélectionnez la ou les dents où positionner les crochets",
              },
            ]}
            setSelected={value => field.onChange(value as string)}
            selected={field.value}
            label="Postionnement"
            dataTestId="hooks-position"
          />
        )}
      />
      {errors.hookChoice && (
        <span style={{ color: "red", fontSize: "14px" }}>
          {errors.hookChoice.message as string}
        </span>
      )}
      {hookChoice === HookPlacementBy.DOCTOR && (
        <Box
          style={{
            display: "flex",
            flexDirection: "column",
            width: "100%",
            alignItems: "center",
          }}
        >
          <Controller
            control={control}
            name="hooksPosition"
            rules={{
              validate: value =>
                (value?.metal?.length ?? 0) > 0 ||
                (value?.valplast?.length ?? 0) > 0 ||
                (value?.acetal?.length ?? 0) > 0 ||
                "Veuillez sélectionner une position",
            }}
            render={({ field }) => (
              <DentalSvgWithButtons
                teethChoices={defaultHooksMaterialOptions}
                onClick={value => {
                  field.onChange(
                    value
                      .map(tooth => ({ [tooth.name]: tooth.teeth }))
                      .reduce((acc, curr) => ({ ...acc, ...curr }), {})
                  );
                }}
                disabledTeeth={disabledTeeth}
                dataTestId="hooks-position-svg"
              />
            )}
          />
          {errors.hooksPosition && (
            <span style={{ color: "red", fontSize: "14px" }}>
              {errors.hooksPosition.message as string}
            </span>
          )}
        </Box>
      )}

      {hookChoice === HookPlacementBy.JASPER && (
        <>
          <Controller
            control={control}
            rules={{ required: "Veuillez sélectionner un matériau" }}
            name="hooksMaterial"
            render={({ field }) => (
              <ButtonRadioSelect
                options={dentalSvgWithButtonsChoices}
                setSelected={value => field.onChange(value)}
                selected={field.value}
                label="Matériau des crochets"
                dataTestId="hooks-material"
              />
            )}
          />
          {errors.hooksMaterial && (
            <span style={{ color: "red", fontSize: "14px" }}>
              {errors.hooksMaterial.message as string}
            </span>
          )}
        </>
      )}
      <Controller
        control={control}
        name="options"
        render={({ field }) => (
          <ButtonRadioSelect
            multiple
            options={options}
            setSelected={value => {
              const selected = field.value ?? [];
              if (selected.includes(value)) {
                field.onChange(selected.filter(o => o !== value));
              } else {
                field.onChange([...selected, value]);
              }
            }}
            selected={field.value ?? []}
            label="Options"
            dataTestId="options"
          />
        )}
      />
    </Box>
  );
};
